import React, { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCurrentDate } from "@/utils/dateHelper";
import { useAchievementsStore, type RealAchievement } from "@/stores/achievementsStore";

/**
 * Global component that handles achievement fetching.
 * Mounted ONCE in the app layout.
 * Controlled via useAchievementsStore.triggerRefresh()
 */
export const AchievementsManager: React.FC = () => {
  // IMPORTANT: Only subscribe to refreshTrigger to avoid re-render loops
  // Access other store methods via getState() to avoid subscriptions
  const refreshTrigger = useAchievementsStore((state) => state.refreshTrigger);

  const { user } = useAuth();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const isClaimingRef = useRef(false);
  const isFetchingRef = useRef(false);
  const lastFetchedRef = useRef(0);

  const claimAchievement = useCallback(
    async (achievement: RealAchievement) => {
      console.log(`🔄 claimAchievement called for: ${achievement.id}`);

      if (!user) {
        console.log("❌ No user, skipping");
        return;
      }

      if (isClaimingRef.current) {
        console.log("⏸️ Already claiming another achievement, skipping");
        return;
      }

      isClaimingRef.current = true;
      try {
        // Fetch fresh profile data to avoid stale points
        const { data: freshProfile, error: profileError } = await supabase
          .from("profiles")
          .select("points")
          .eq("id", user.id)
          .single();

        if (profileError || !freshProfile) {
          console.error("❌ Error fetching fresh profile:", profileError);
          return;
        }

        console.log("💰 Adding", achievement.pointsReward, "XP points via earn_points RPC");

        const { error: rpcErr } = await supabase.rpc("earn_points", {
          p_user_id: user.id,
          p_amount: achievement.pointsReward,
          p_description: `Achievement: ${achievement.id}`,
        });

        if (rpcErr) {
          console.error("❌ RPC earn_points failed:", rpcErr);
          return;
        }

        // Mark achievement as claimed
        const { error: upsertError } = await supabase.from("tutor_achievements").upsert(
          {
            user_id: user.id,
            achievement_id: achievement.id,
            completed: true,
            progress: 100,
            completed_at: getCurrentDate().toISOString(),
          },
          {
            onConflict: "user_id,achievement_id",
            ignoreDuplicates: false,
          }
        );

        if (upsertError) {
          console.error("❌ Error upserting achievement:", upsertError);
          if (upsertError.code === "23505") {
            console.log("⚠️ Achievement already claimed, skipping");
            return;
          }
          return;
        }

        toast({
          title: t("Odměna získána!", "Odmena získaná!"),
          description: t(
            `Získal jsi ${achievement.pointsReward} bodů za splnění úkolu: ${achievement.title}.`,
            `Získal si ${achievement.pointsReward} bodov za splnenie úlohy: ${achievement.title}.`
          ),
        });
      } catch (error) {
        console.error("Error claiming reward:", error);
        toast({
          title: t("Chyba", "Chyba"),
          description: t("Nepodařilo se získat odměnu.", "Nepodarilo sa získať odmenu."),
          variant: "destructive",
        });
      } finally {
        setTimeout(() => {
          isClaimingRef.current = false;
        }, 1000);
      }
    },
    [user, toast, t]
  );

  const loadAchievements = useCallback(
    async (isInitial = false) => {
      // Basic guard: user must be logged in
      if (!user) return;

      // Guard: Prevent re-entry if already fetching or claiming (use refs to avoid re-renders)
      if (isFetchingRef.current || isClaimingRef.current) {
        console.log("⏭️ Manager skipping loadAchievements (busy)");
        return;
      }

      // Global throttle: prevent network spam (use ref to avoid re-renders)
      const now = Date.now();
      if (now - lastFetchedRef.current < 2000 && !isInitial) {
        console.log("⏭️ Manager skipping loadAchievements due to throttle (2s)");
        return;
      }

      isFetchingRef.current = true;
      const store = useAchievementsStore.getState();

      // Show loading only on first load
      if (!store.achievements.length) {
        store.setIsLoading(true);
      }
      store.setIsFetching(true);

      try {
        // Verify we have a valid session before calling
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          console.log("⏭️ Manager skipping - no valid session");
          return;
        }

        console.log("🚀 Manager calling check-achievements Edge Function...");

        const fetchPromise = supabase.functions.invoke("check-achievements", {
          body: { language },
        });

        const timeoutPromise = new Promise<{ data: any; error: any }>((_, reject) =>
          setTimeout(() => reject(new Error("Timeout waiting for check-achievements")), 25000)
        );

        const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);

        if (error) throw error;
        if (!data || !data.achievements) throw new Error("No data returned");

        const realAchievements: RealAchievement[] = data.achievements;

        // Auto-claim logic
        const unclaimedAchievements = realAchievements.filter(
          (ach) => ach.isCompleted && !ach.isClaimed
        );

        if (unclaimedAchievements.length > 0) {
          const ach = unclaimedAchievements[0];
          console.log(`🎉 Manager auto-claiming: ${ach.id}`);
          await claimAchievement(ach);
          ach.isClaimed = true;
        }

        useAchievementsStore.getState().setAchievements([...realAchievements]);
        lastFetchedRef.current = Date.now();
        useAchievementsStore.getState().setLastFetched(lastFetchedRef.current);
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        const isAuthError =
          msg.toLowerCase().includes("refresh token") ||
          msg.toLowerCase().includes("jwt") ||
          msg.toLowerCase().includes("not authenticated") ||
          msg.toLowerCase().includes("invalid claim");

        if (isAuthError) {
          console.warn("⏭️ Manager skipping achievements – session expired");
        } else {
          console.error("❌ Manager error loading achievements:", error);
        }
      } finally {
        isFetchingRef.current = false;
        useAchievementsStore.getState().setIsLoading(false);
        useAchievementsStore.getState().setIsFetching(false);
      }
    },
    [user, language, claimAchievement]
  );

  // Initial load on mount - only depends on user
  useEffect(() => {
    if (user) {
      console.log("🔌 AchievementsManager mounted - initial check");
      loadAchievements(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Listen to store triggers - only depends on refreshTrigger
  useEffect(() => {
    if (refreshTrigger > 0 && user) {
      console.log("🔔 AchievementsManager: Manual trigger received:", refreshTrigger);
      loadAchievements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  return null;
};
